import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

const CONFIRM_RATIO = 0.85;

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

type Props = {
  disabled?: boolean;
  loading?: boolean;
  onConfirm: () => Promise<void> | void;
};

export default function SlideToConfirm({ disabled = false, loading = false, onConfirm }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLButtonElement>(null);
  const [slidePx, setSlidePx] = useState(0);
  const slidePxRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const dragActiveRef = useRef(false);
  const dragStart = useRef({ clientX: 0, slide: 0 });

  const thumbOuter = 52;
  const trackPad = 6;

  const maxSlide = useCallback(() => {
    const w = trackRef.current?.getBoundingClientRect().width ?? 0;
    return Math.max(0, w - thumbOuter - trackPad * 2);
  }, []);

  useEffect(() => {
    slidePxRef.current = slidePx;
  }, [slidePx]);


  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    e.preventDefault();
    thumbRef.current?.setPointerCapture(e.pointerId);
    dragActiveRef.current = true;
    setDragging(true);
    dragStart.current = { clientX: e.clientX, slide: slidePxRef.current };
  };

  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (!dragActiveRef.current) return;
    const m = maxSlide();
    const dx = e.clientX - dragStart.current.clientX;
    setSlidePx(clamp(dragStart.current.slide + dx, 0, m));
  };

  const finishDrag = useCallback(async (e: PointerEvent<HTMLButtonElement>) => {
    try {
      thumbRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    if (!dragActiveRef.current) return;
    dragActiveRef.current = false;
    setDragging(false);
    const m = maxSlide();
    const pos = slidePxRef.current;
    const okSlide = m > 0 && pos >= m * CONFIRM_RATIO;
    if (okSlide && !disabled && !loading) await Promise.resolve(onConfirm());
    setSlidePx(0);
  }, [disabled, loading, maxSlide, onConfirm]);

  return (
    <div className="space-y-2">
      <div
        ref={trackRef}
        className={`relative h-[3.25rem] w-full overflow-hidden rounded-full border-2 border-gray-300 bg-gray-100 ${
          disabled || loading ? 'opacity-50' : ''
        }`}
      >
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-14 text-center text-xs font-medium text-gray-600 sm:text-sm">
          {loading ? 'Сохранение…' : 'Потяните вправо, чтобы подтвердить оплату'}
        </div>
        <button
          ref={thumbRef}
          type="button"
          disabled={disabled || loading}
          className={`absolute top-1 flex h-[2.75rem] w-[3.25rem] cursor-grab touch-none items-center justify-center rounded-full bg-emerald-600 text-lg font-bold text-white shadow-md active:cursor-grabbing ${
            dragging ? '' : 'transition-[transform] duration-200 ease-out'
          }`}
          style={{ left: trackPad, transform: `translateX(${slidePx}px)` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => void finishDrag(e)}
          onPointerCancel={(e) => void finishDrag(e)}
          aria-label="Потяните вправо для подтверждения"
        >
          »»
        </button>
      </div>
      <p className="text-center text-[11px] text-gray-500">Отпустите ползунок в конце для подтверждения</p>
    </div>
  );
}
